"use client";

/**
 * The Next.js implementation of chat's routing seam.
 *
 * Lives in the APP, not the widget: it is the one consumer that owns a whole
 * page and therefore has the right to drive the address bar. See
 * src/core/routing.ts for why the widget refuses to do this itself.
 *
 * WHY history.pushState AND NOT router.push
 *
 * `router.push` re-runs the App Router's navigation machinery — it remounts the
 * route tree, which for a single-page chat means tearing down and rebuilding
 * the entire conversation (and its scroll position, and any in-flight stream)
 * to change a query string. Every route in this app renders the SAME component,
 * so there is nothing for the router to fetch or swap. `pushState` updates the
 * URL and nothing else, which is precisely the intent.
 *
 * The trade-off is that `pushState` notifies nothing, so this adapter emits its
 * own event — see below.
 */

import type { ChatRoute, ChatRouting } from "@/src/core/routing";
import { buildHref, parseRoute } from "@/src/core/routing";

/**
 * Our own navigation signal.
 *
 * `popstate` fires for back/forward but NOT for pushState/replaceState, so
 * without this a URL we set ourselves would be invisible to subscribers. Both
 * are needed: popstate for the user's navigation, this for ours.
 */
const ROUTE_EVENT = "chat:routechange";

function currentRoute(): ChatRoute {
  if (typeof window === "undefined") return {};
  const { pathname, search, hash } = window.location;
  return parseRoute(pathname, search, hash);
}

export function createNextRouting(): ChatRouting {
  return {
    read: currentRoute,

    navigate(route, opts) {
      if (typeof window === "undefined") return;
      const href = buildHref(route);
      // Skipping a no-op write matters: pushing an identical URL still adds a
      // history entry, which turns the back button into a row of stops on the
      // same page.
      const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (href === here) return;

      if (opts?.replace) window.history.replaceState(null, "", href);
      else window.history.pushState(null, "", href);

      window.dispatchEvent(new CustomEvent(ROUTE_EVENT));
    },

    subscribe(onChange) {
      if (typeof window === "undefined") return () => {};
      const handler = () => onChange(currentRoute());
      window.addEventListener("popstate", handler);
      window.addEventListener(ROUTE_EVENT, handler);
      return () => {
        window.removeEventListener("popstate", handler);
        window.removeEventListener(ROUTE_EVENT, handler);
      };
    },
  };
}
